<template>
  <article class="flex w-full h-full" data-component="CardEventCompact">
    <AtomLink
      :url="url"
      class="group grid grid-rows-[auto_1fr] w-full h-full rounded-3xl overflow-hidden shadow-lg hover:shadow-none transform transition duration-200"
      @click="onClick"
    >
      <figure class="row-span-1 relative">
        <span
          v-if="when"
          class="absolute z-10 bg-app-primary-1-red--fire-brick top-0 left-0 rounded-br-3xl text-white font-semibold text-sm lg:text-lg leading-none px-5 py-3 lg:px-8 lg:py-3"
        >
          {{ $d(when, 'long-date') }}
        </span>
        <MoleculePicture class="block w-full h-full object-cover transform transition duration-200 group-hover:scale-110 group-focus:scale-110" :image="image" :alt="imgAlt" />
      </figure>
      <section class="c-content relative row-span-1 flex">
        <div class="z-20 bg-white flex flex-1 flex-col justify-start px-5 pb-5">
          <h3 class="c-content__title mr-10 text-base sm:text-lg font-semibold leading-snug text-black mb-2">
            {{ title }}
          </h3>
          <div v-if="featuredWorks.length > 0" class="text-xs leading-tight">
            <span v-if="featuredWorks.length > 1" class="block mb-1 italic">{{ t_('including') }}</span>
            <ul class="list-inside list-none">
              <li v-for="(work, index) in featuredWorks" :key="index" class="mb-1 c-content-list-item">
                <!-- The {{ ' ' }} below is a real space rather than a margin, so it survives copy-paste and
                     screen readers: Vue 3's compiler drops the newline between the spans that provided it. -->
                <span v-if="work.composers.length > 0" class="font-medium">{{ work.composers[0].title }}</span
                >{{ ' ' }}<span class="italic">{{ work.parent && work.parent.id ? `${work.title} ${work.parent.title}` : work.title }}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </AtomLink>
  </article>
</template>

<script setup lang="ts">
const props = defineProps({
  url: { type: String, required: true },
  image: {
    type: Object as PropType<Record<string, any>>,
    required: false,
    default: () => ({ _smUrl: '/images/fallback/345x206.png', _smWidth: 908, _smHeight: 545, _mdUrl: '/images/fallback/463x280.png', _mdWidth: 947, _mdHeight: 573 }),
  },
  imgAlt: { type: String, required: false, default: undefined },
  title: { type: String, required: true },
  when: { type: Date, required: false, default: undefined },
  works: { type: Array as PropType<any[]>, required: false, default: () => [] },
})

const emit = defineEmits(['click'])

const { t_ } = setupTranslationPrefixer('CardEventAndSeries')

const featuredWorks = computed(() =>
  [...props.works]
    .filter((work) => !work.encore)
    .sort((a, b) => a.featured - b.featured)
    .slice(-2)
    .reverse(),
)

function onClick(event: Event) {
  emit('click', event)
}
</script>

<style lang="scss" scoped>
$side-size-svg: 49px;

.c-content {
  @apply z-10;
  padding-top: calc(#{$side-size-svg} - 1px);
  margin-top: calc(-1 * (#{$side-size-svg} - 1px));

  &::before {
    content: '';
    background-image: url('/images/brand/BrandLyreCardHorizontal.svg');
    width: 84px;
    height: $side-size-svg;
    @apply absolute top-0 right-0 bg-no-repeat bg-cover;
  }

  &::after {
    content: '';
    width: calc(100% - 84px + 1px);
    height: $side-size-svg;
    @apply absolute top-0 left-0 bg-white;
  }
}

.c-content__title {
  margin-top: calc(-1 * theme('spacing.8'));
}

.c-content-list-item {
  position: relative;
  padding-left: 12px;

  &::marker {
    display: none;
  }
  &::before {
    content: '·';
    position: absolute;
    top: -2px;
    left: 0;
    font-size: 20px;
    line-height: 1;
  }
}
</style>
